<template>
  <el-container v-loading="loading"
                class="module-container image-navigator"
                direction="vertical">
    <MobileFilterButton :visits-module="true" />
    <el-header class="module-container__header">
      <div class="module-header-top">
        <div class="title">
          {{ $t('IMAGE NAVIGATOR') }}
        </div>
      </div>
    </el-header>
    <div class="image-navigator__wrapper">
      <div class="image-navigator__focus">
        <el-switch v-model="focusMode"
                   active-color="#409eff"
                   :active-text=" $t('Focus Mode')">
          {{ $t('Focus Mode') }}
        </el-switch>
      </div>
      <div v-if="stores.length > 0" class="image-navigator__images">
        <StoreItem v-for="store in stores"
                   :key="store.id"
                   :store="store"
                   :max-image-count-per-row="maxImageCountPerRow"
                   :image-box-width="imageBoxWidth"
                   :wrapper-width="wrapperWidth" />
      </div>
      <div v-else class="image-navigator__nodata">
        <span>
          {{ $t("Your search resulted with no data.") }}
        </span>
      </div>
      <el-pagination
        v-if="stores.length > 0"
        :page-size="pagination.pageSize"
        :current-page="pagination.currentPage"
        :layout="paginationOptions.layout"
        :page-sizes="paginationOptions.pageSizes"
        :total="meta.total"
        @prev-click="handlePrevNext"
        @next-click="handlePrevNext"
        @size-change="handleSizeChange">
        <span class="el-pagination__custom-current-page">
          {{ $t('Page') }} {{ pagination.currentPage }}
        </span>
      </el-pagination>
    </div>
  </el-container>
</template>

<script>
import { result } from 'lodash';
import { mapState } from 'vuex';
import WorkWithDashboardFilter from '@/helpers/mixins/workWithDashboardFilter';
import mobileScreenControl from '@/helpers/mixins/mobileScreenControl';
import MobileFilterButton from '@/components/MobileFilterButton';
import { fetchImageNavigator } from './api';
import BackendPaginationMixin from '../../helpers/mixins/backendPagination';
import StoreItem from './components/StoreItem';

export default {
  name: 'OperationsDashboard',
  components: {
    MobileFilterButton,
    StoreItem
  },
  mixins: [BackendPaginationMixin, WorkWithDashboardFilter, mobileScreenControl],
  props: {
    projectId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      pagination: {
        pageSize: 5,
        currentPage: 1,
        sort: '',
        order: ''
      },
      paginationOptions: {
        pageSizes: [5, 10, 15]
      },
      meta: {},
      focusMode: true,
      stores: [],
      loading: true,
      wrapperWidth: 0,
      wrapper: null,
      resizeObserver: null
    };
  },
  computed: {
    ...mapState(['Project']),
    objectionStatisticsVisible() {
      return result(this.Project.active.data, 'settings.objections_statistic.show', false);
    },
    fetchParams() {
      const { selectedFilterScopes, start_time: clientStartTime, end_time: clientEndTime } = this.GlobalFilters.query;
      const start_date_with_time = this.convertToServerTimeZone(`${clientStartTime} 00:00:00`);
      const end_date_with_time = this.convertToServerTimeZone(`${clientEndTime} 23:59:59`);
      const filterScopes = JSON.parse(JSON.stringify(selectedFilterScopes));
      const { image_filters } = filterScopes;

      delete filterScopes.image_filters;

      if (image_filters && !image_filters.search) delete image_filters.search;

      const params = {
        ...this.getQuery,
        filter: {
          start_date_with_time,
          end_date_with_time,
          ...filterScopes
        },
        ...((image_filters && this.focusMode) && {
          ...image_filters
        })
      };

      return params;
    },
    maxImageCountPerRow() {
      let count = 2;
      const windowWidth = window.innerWidth;

      if (windowWidth > 1536) count = 12;
      else if (windowWidth > 1280) count = 10;
      else if (windowWidth > 1024) count = 8;
      else if (windowWidth > 768) count = 4;

      return count;
    },
    imageBoxWidth() {
      return (this.wrapperWidth - 150) / this.maxImageCountPerRow;
    }
  },
  watch: {
    focusMode() {
      this.fetchData();
    }
  },
  mounted() {
    this.fetchData();

    [this.wrapper] = document.getElementsByClassName('image-navigator__wrapper');

    this.resizeObserver = new ResizeObserver(this.setWrapperWidth);
    this.resizeObserver.observe(this.wrapper);
  },
  beforeDestroy() {
    this.resizeObserver.unobserve(this.wrapper);
  },
  methods: {
    async fetchData() {
      this.loading = true;
      const { projectId, fetchParams } = this;
      const { data: { data, meta } } = await fetchImageNavigator(projectId, fetchParams).finally(() => { this.loading = false; });

      // alphabetical sorting
      const sortedData = data.map((store) => {
        const image_sets = store.image_sets.sort((a, b) => a.activity_category_name.localeCompare(b.activity_category_name));

        return { ...store, image_sets };
      });

      this.stores = sortedData;
      this.meta = meta;

      window.scrollTo(0, 0);
    },
    setWrapperWidth(e) {
      this.wrapperWidth = e[0].target.offsetWidth;
    }
  }
};
</script>

<style lang="scss">
.image-navigator {
  position: relative;

  &__wrapper {
    padding: 12px;
  }

  &__focus {
    margin-bottom: 12px;
    margin-left: 30px;
  }

  &__nodata {
    display: flex;
    justify-content: center;
    margin: 20px 0;

    span {
      display: inline-block;
      padding: 30px;
      font-weight: 700;
      color: #999;
      text-align: center;
      background: #fff;
      border-radius: 10px;
    }
  }

  @media (max-width: 800px) {
    .title {
      padding-left: 30px;
    }

    &__focus {
      margin-left: 0;
    }
  }
}
</style>
